import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import telegram from '../utils/telegram';
import api from '../api';
import FeedbackModal from '../components/FeedbackModal';
import '../styles/Testing.css';

// Default fragmentation configs for different countries
const DEFAULT_FRAGMENTATION = {
  'NL': {
    fragment: 1,
    packets: '1-3',
    length: '15-25',
    interval: '2-5',
    description: 'Рекомендуемая конфигурация для защиты соединения'
  },
  'US': {
    fragment: 1,
    packets: 'tlshello',
    length: '30-50',
    interval: '5-10',
    description: 'Стабильная конфигурация'
  },
  'DE': {
    fragment: 1,
    packets: '1-3',
    length: '20-30',
    interval: '3-7',
    description: 'Оптимальная конфигурация для Европы'
  },
  'default': {
    fragment: 1,
    packets: '1-3',
    length: '15-25',
    interval: '2-5',
    description: 'Универсальная конфигурация'
  }
};

const PROVIDERS = {
  mts: { name: 'МТС', logo: '/images/providers/mts.png' },
  megafon: { name: 'Мегафон', logo: '/images/providers/megafon.png' },
  beeline: { name: 'Билайн', logo: '/images/providers/beeline.png' },
  rostelecom: { name: 'Ростелеком', logo: '/images/providers/rostelecom.png' },
  tele2: { name: 'Tele2', logo: '/images/providers/tele2.png' },
  wifi: { name: 'WiFi', logo: '/images/providers/wifi.png' }
};

function Testing() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [points, setPoints] = useState(0);
  const [activeTab, setActiveTab] = useState('quick'); // 'quick' or 'advanced'

  // Servers state
  const [servers, setServers] = useState([]);
  const [loadingServers, setLoadingServers] = useState(true);

  // Quick Config state
  const [selectedQuickConfig, setSelectedQuickConfig] = useState(null);

  // Advanced state
  const [selectedAdvancedServer, setSelectedAdvancedServer] = useState(null);
  const [configUrl, setConfigUrl] = useState('https://ams3.uspn.io/go/test');
  const [fragment, setFragment] = useState(1);
  const [packets, setPackets] = useState('1-3');
  const [length, setLength] = useState('15-25');
  const [interval, setInterval] = useState('2-5');
  const [protocols, setProtocols] = useState(['vless', 'trojan']);

  // Testing state
  const [provider, setProvider] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [networkType, setNetworkType] = useState('wifi');

  // Instruction modal
  const [showInstruction, setShowInstruction] = useState(false);

  // Save configuration modal
  const [showSavePrompt, setShowSavePrompt] = useState(false);

  // Results
  const [generatedConfig, setGeneratedConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Feedback modal
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    message: '',
    type: 'success'
  });

  useEffect(() => {
    // Get user from WebApp
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      const userData = tg.initDataUnsafe?.user;
      if (userData) {
        setUser(userData);
      }
    }

    loadServers();
    loadStats();
    loadUserSettings();
    loadUserPoints();
  }, []);

  const loadUserPoints = async () => {
    try {
      const veraRes = await api.get('/vera/balance');
      if (veraRes.data) {
        setPoints(veraRes.data.total_points || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки очков:', error);
      setPoints(0);
    }
  };

  const loadServers = async () => {
    try {
      setLoadingServers(true);
      const response = await api.get('/servers/list', {
        params: { active_only: true }
      });

      const serversList = response.data.servers || [];
      setServers(serversList);

      // Set first server as default selection for both modes
      if (serversList.length > 0) {
        if (!selectedQuickConfig) {
          setSelectedQuickConfig(serversList[0].id);
        }
        if (!selectedAdvancedServer) {
          setSelectedAdvancedServer(serversList[0].id);
          setConfigUrl(serversList[0].subscription_base_url);
        }
      }
    } catch (error) {
      console.error('Failed to load servers:', error);
      setServers([]);
    } finally {
      setLoadingServers(false);
    }
  };

  const loadUserSettings = async () => {
    try {
      const { getUserSettings } = await import('../api');
      const response = await getUserSettings();
      const settings = response.data;

      // Подставляем провайдера из настроек
      if (settings.provider) {
        setProvider(settings.provider);
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  };

  const loadStats = async () => {
    try {
      // Используем новый эндпоинт статистики по фидбеку
      const response = await api.get('/feedback/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      // Не критично, просто не показываем статистику
    }
  };

  const getSelectedServer = () => {
    return servers.find(s => s.id === selectedQuickConfig);
  };

  const getAdvancedSelectedServer = () => {
    return servers.find(s => s.id === selectedAdvancedServer);
  };

  const getFragmentationConfig = (countryCode) => {
    return DEFAULT_FRAGMENTATION[countryCode] || DEFAULT_FRAGMENTATION['default'];
  };

  const handleServerChange = (serverId) => {
    setSelectedAdvancedServer(serverId);
    const server = servers.find(s => s.id === serverId);
    if (server) {
      setConfigUrl(server.subscription_base_url);
    }
  };

  const buildTestUrl = () => {
    let base, frag, pack, len, int;

    if (activeTab === 'quick') {
      const server = getSelectedServer();
      if (!server) return '';

      base = server.subscription_base_url;
      const fragConfig = getFragmentationConfig(server.country_code);
      frag = fragConfig.fragment;
      pack = fragConfig.packets;
      len = fragConfig.length;
      int = fragConfig.interval;
    } else {
      base = configUrl;
      frag = fragment;
      pack = packets;
      len = length;
      int = interval;
    }

    const params = new URLSearchParams();

    if (frag) {
      params.append('fragment', frag);
      params.append('packets', pack);
      params.append('length', len);
      params.append('interval', int);
    }

    return `${base}?${params.toString()}`;
  };

  const parseUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);

      setConfigUrl(urlObj.origin + urlObj.pathname);
      setFragment(params.get('fragment') === '1' ? 1 : 0);
      setPackets(params.get('packets') || '1-3');
      setLength(params.get('length') || '15-25');
      setInterval(params.get('interval') || '2-5');
    } catch (error) {
      console.error('Invalid URL:', error);
    }
  };

  const runTest = async () => {
    setLoading(true);
    setTestResult(null);
    setShowSavePrompt(false);

    const testUrl = buildTestUrl();
    const startTime = Date.now();

    try {
      await fetch(testUrl, { mode: 'no-cors' });
      const duration = Date.now() - startTime;

      setTestResult({
        success: true,
        duration,
        url: testUrl,
        timestamp: new Date().toLocaleTimeString('ru-RU')
      });

      // Show save prompt after successful test
      setShowSavePrompt(true);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message,
        url: testUrl,
        timestamp: new Date().toLocaleTimeString('ru-RU')
      });
    } finally {
      setLoading(false);
    }
  };

  const generateConfig = async () => {
    if (protocols.length === 0) {
      alert('Выберите хотя бы один протокол');
      return;
    }

    setLoading(true);
    setShowSavePrompt(false);

    try {
      // Генерируем URL локально
      const testUrl = buildTestUrl();
      const server = getAdvancedSelectedServer();

      // Create connection link through API
      const response = await api.post('/happ/generate-link', {
        subscription_url: testUrl,
        provider: provider,
        time_period: timeOfDay,
        country: server?.country_code || 'NL',
        protocols: protocols,
        source: 'testing_advanced'
      });

      setGeneratedConfig({
        subscription_url: testUrl,
        connection_url: response.data.connection_url,
        config_params: {
          fragment,
          packets,
          length,
          interval,
          protocols,
          provider,
          time_of_day: timeOfDay,
          network_type: networkType
        }
      });

      // Show save prompt after generating config
      setShowSavePrompt(true);
    } catch (error) {
      console.error('Failed to generate connection URL:', error);
      // Fallback: still show config without connection_url
      const testUrl = buildTestUrl();
      setGeneratedConfig({
        subscription_url: testUrl,
        config_params: {
          fragment,
          packets,
          length,
          interval,
          protocols,
          provider,
          time_of_day: timeOfDay,
          network_type: networkType
        }
      });
      setShowSavePrompt(true);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async (silent = false) => {
    if (!user) {
      if (!silent) {
        setFeedbackModal({
          isOpen: true,
          message: 'Необходима авторизация в Telegram',
          type: 'warning'
        });
      }
      return false;
    }

    const testUrl = generatedConfig?.subscription_url || testResult?.url || buildTestUrl();
    const server = activeTab === 'quick' ? getSelectedServer() : getAdvancedSelectedServer();

    try {
      const response = await api.post('/saved-configs/save', {
        user_id: user.id,
        name: `Конфигурация ${server?.country_name || 'Сервера'}`,
        subscription_url: testUrl,
        config_params: {
          fragment,
          packets,
          length,
          interval,
          protocols: activeTab === 'advanced' ? protocols : ['vless', 'trojan'],
          provider,
          time_of_day: timeOfDay,
          network_type: networkType,
          server_id: server?.id
        }
      });

      if (response.data.success || response.data.id) {
        if (!silent) {
          setFeedbackModal({
            isOpen: true,
            message: 'Конфигурация успешно сохранена в избранное',
            type: 'success'
          });
        }
        setShowSavePrompt(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to save configuration:', error);
      if (!silent) {
        const errorMessage = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
        setFeedbackModal({
          isOpen: true,
          message: `Ошибка сохранения: ${errorMessage}`,
          type: 'error'
        });
      }
      return false;
    }
  };

  const submitFeedback = async (works) => {
    if (!generatedConfig && !testResult) return;
    if (!user) {
      setFeedbackModal({
        isOpen: true,
        message: 'Необходима авторизация в Telegram',
        type: 'warning'
      });
      return;
    }
    if (!provider) {
      setFeedbackModal({
        isOpen: true,
        message: 'Выберите провайдера для получения очков',
        type: 'warning'
      });
      return;
    }

    // Haptic feedback
    telegram.hapticMedium();

    try {
      const testUrl = generatedConfig?.subscription_url || testResult?.url || buildTestUrl();

      const response = await api.post('/feedback/submit', {
        user_id: user.id,
        provider: provider,
        time_period: timeOfDay || 'day',
        server_id: 1, // Default server ID
        subscription_url: testUrl,
        works: works,
        connection_speed: testResult?.duration < 500 ? 'fast' : testResult?.duration < 2000 ? 'medium' : 'slow',
        comment: null
      });

      if (response.data.success) {
        const pointsAwarded = response.data.points_awarded;
        const totalPoints = response.data.total_points;

        // Если работает - автоматически сохранить конфигурацию (silent mode)
        if (works) {
          await saveConfiguration(true);
          telegram.hapticNotification('success');
        } else {
          telegram.hapticNotification('warning');
        }

        // Show success modal with points
        const modalMessage = works
          ? `Конфигурация сохранена!\n\nНачислено: +${pointsAwarded} VERA баллов\nВсего: ${totalPoints} баллов`
          : `Спасибо за отзыв!\n\nНачислено: +${pointsAwarded} баллов\nВсего: ${totalPoints} баллов`;

        setFeedbackModal({
          isOpen: true,
          message: modalMessage,
          type: 'success'
        });

        loadStats();

        // Обновить очки на главной странице
        window.dispatchEvent(new CustomEvent('pointsUpdated', { detail: totalPoints }));

        // Hide save prompt since we saved automatically
        setShowSavePrompt(false);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
      setFeedbackModal({
        isOpen: true,
        message: `Ошибка отправки отзыва:\n${errorMessage}`,
        type: 'error'
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('📋 Скопировано!');
    }).catch(() => {
      alert('Ошибка копирования');
    });
  };

  const toggleProtocol = (protocol) => {
    if (protocols.includes(protocol)) {
      setProtocols(protocols.filter(p => p !== protocol));
    } else {
      setProtocols([...protocols, protocol]);
    }
  };

  const handleTelegramClick = () => {
    telegram.hapticLight();
    telegram.openTelegramLink('https://t.me/+wBXI4N49pwdhNDY1');
  };

  const handleNavigation = (path) => {
    telegram.hapticLight();
    navigate(path);
  };

  return (
    <div className="testing-page">
      {/* Header with Points */}
      <div className="testing-header">
        <div
          className="points-display clickable"
          onClick={() => handleNavigation('/profile')}
        >
          <span className="points-icon">⭐</span>
          <span className="points-value">{points}</span>
          <span className="points-label">VERA</span>
        </div>
        <div className="header-right">
          <div className="user-greeting">
            {user?.first_name || 'Гость'} 👋
          </div>
          <button className="telegram-chat-btn" onClick={handleTelegramClick} title="Присоединиться к чату">
            <svg className="telegram-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18.717-1.558 7.315-2.2 9.712-.271.996-.806 1.33-1.321 1.362-.563.035-1.175-.232-1.823-.58-1.011-.543-1.582-.88-2.563-1.41-1.133-.612-.399-1.013.247-1.601.169-.154 3.107-2.85 3.165-3.094.007-.031.014-.145-.054-.206s-.164-.04-.234-.024c-.1.024-1.688 1.073-4.764 3.149-.451.311-.86.462-1.226.454-.404-.009-1.18-.228-1.758-.416-.709-.23-1.272-.352-1.223-.743.025-.203.31-.41.855-.62 3.35-1.458 5.583-2.419 6.7-2.882 3.19-1.326 3.852-1.556 4.285-1.563.095-.002.307.022.445.134.116.094.148.221.163.31.015.088.034.29.019.447z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Title Section */}
      <div className="testing-header-compact">
        <h1>🧪 Тестирование</h1>
        <p>Помоги улучшить сервис и получай очки</p>
      </div>

      {/* Quick Access to Configurations */}
      <div className="quick-access-section">
        <button
          className="configs-quick-btn"
          onClick={() => handleNavigation('/configurations')}
        >
          <span className="configs-icon">🌐</span>
          <span className="configs-text">Готовые конфиги</span>
          <span className="configs-arrow">→</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-compact">
        <button
          className={`tab-compact ${activeTab === 'quick' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('quick');
            // Clear results when switching tabs
            setTestResult(null);
            setGeneratedConfig(null);
            setShowSavePrompt(false);
          }}
        >
          ⚡ Быстро
        </button>
        <button
          className={`tab-compact ${activeTab === 'advanced' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('advanced');
            // Clear results when switching tabs
            setTestResult(null);
            setGeneratedConfig(null);
            setShowSavePrompt(false);
          }}
        >
          🔧 Расширенный
        </button>
      </div>

      {/* Quick Config Tab */}
      {activeTab === 'quick' && (
        <div className="panel-compact">
          <h3>Быстрые конфигурации</h3>
          {loadingServers ? (
            <div className="loading-text">Загрузка серверов...</div>
          ) : servers.length === 0 ? (
            <div className="no-servers">Нет доступных серверов</div>
          ) : (
            <div className="quick-configs">
              {servers.map(server => {
                const fragConfig = getFragmentationConfig(server.country_code);
                return (
                  <div
                    key={server.id}
                    className={`quick-config-card ${selectedQuickConfig === server.id ? 'active' : ''}`}
                    onClick={() => setSelectedQuickConfig(server.id)}
                  >
                    <div className="config-name">
                      {server.country_name} {server.country_flag}
                    </div>
                    <div className="config-desc">{fragConfig.description}</div>
                    <div className="config-params">
                      <span>fragment: {fragConfig.fragment}</span>
                      <span>packets: {fragConfig.packets}</span>
                      <span>length: {fragConfig.length}</span>
                      <span>interval: {fragConfig.interval}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Provider Selection */}
          <div className="form-section-compact">
            <label>Ваш провайдер (для получения очков)</label>
            <div className="provider-grid-compact">
              {Object.entries(PROVIDERS).map(([key, providerData]) => (
                <button
                  key={key}
                  className={`provider-btn ${provider === key ? 'selected' : ''}`}
                  onClick={() => setProvider(key === provider ? '' : key)}
                >
                  <div className="provider-logo-wrapper-small">
                    <img src={providerData.logo} alt={providerData.name} className="provider-logo-small" />
                  </div>
                  <span className="provider-label">{providerData.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Time of Day Selection */}
          <div className="form-section-compact">
            <label>Время суток</label>
            <div className="time-period-buttons">
              <button
                className={`time-btn ${timeOfDay === 'night' ? 'selected' : ''}`}
                onClick={() => setTimeOfDay(timeOfDay === 'night' ? '' : 'night')}
              >
                🌙 Ночь
              </button>
              <button
                className={`time-btn ${timeOfDay === 'day' ? 'selected' : ''}`}
                onClick={() => setTimeOfDay(timeOfDay === 'day' ? '' : 'day')}
              >
                ☀️ День
              </button>
              <button
                className={`time-btn ${timeOfDay === 'evening' ? 'selected' : ''}`}
                onClick={() => setTimeOfDay(timeOfDay === 'evening' ? '' : 'evening')}
              >
                🌆 Вечер
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Advanced Tab */}
      {activeTab === 'advanced' && (
        <div className="panel-compact">
          <div className="section-header">
            <h3>Расширенные настройки</h3>
            <button
              className="help-button"
              onClick={() => setShowInstruction(!showInstruction)}
              title="Показать инструкцию"
            >
              ❓
            </button>
          </div>

          {/* Instruction Panel */}
          {showInstruction && (
            <div className="instruction-panel">
              <h4>💡 Как подобрать конфигурацию</h4>
              <ul>
                <li><strong>Выберите сервер</strong> из списка доступных</li>
                <li><strong>Включите фрагментацию</strong> для защиты соединения</li>
                <li><strong>Настройте параметры:</strong>
                  <ul>
                    <li>Packets - количество пакетов для обработки</li>
                    <li>Length - размер фрагментов (15-100 байт)</li>
                    <li>Interval - задержка между пакетами (мс)</li>
                  </ul>
                </li>
                <li><strong>Выберите провайдера</strong> для точной настройки</li>
                <li><strong>Запустите тест</strong> и проверьте скорость</li>
                <li><strong>Сохраните</strong> рабочую конфигурацию</li>
              </ul>
            </div>
          )}

          {/* Server Selection */}
          {!loadingServers && servers.length > 0 && (
            <div className="form-group-compact">
              <label>🌐 Выбор сервера:</label>
              <div className="server-selector">
                {servers.map(server => (
                  <button
                    key={server.id}
                    className={`server-option ${selectedAdvancedServer === server.id ? 'selected' : ''}`}
                    onClick={() => handleServerChange(server.id)}
                  >
                    <span className="server-flag">{server.country_flag}</span>
                    <span className="server-name">{server.country_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fragment Toggle */}
          <div className="form-group-compact">
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={fragment === 1}
                onChange={(e) => setFragment(e.target.checked ? 1 : 0)}
              />
              <span>Включить фрагментацию</span>
            </label>
          </div>

          {fragment === 1 && (
            <div className="params-grid-compact">
              <div className="form-group-compact">
                <label>Packets:</label>
                <select value={packets} onChange={(e) => setPackets(e.target.value)}>
                  <option value="tlshello">tlshello</option>
                  <option value="1-3">1-3</option>
                  <option value="1-5">1-5</option>
                </select>
              </div>
              <div className="form-group-compact">
                <label>Length:</label>
                <select value={length} onChange={(e) => setLength(e.target.value)}>
                  <option value="15-25">15-25</option>
                  <option value="30-50">30-50</option>
                  <option value="50-100">50-100</option>
                </select>
              </div>
              <div className="form-group-compact">
                <label>Interval:</label>
                <select value={interval} onChange={(e) => setInterval(e.target.value)}>
                  <option value="2-5">2-5</option>
                  <option value="5-10">5-10</option>
                  <option value="10-20">10-20</option>
                </select>
              </div>
            </div>
          )}

          {/* Protocols */}
          <div className="form-group-compact">
            <label>Протоколы:</label>
            <div className="protocol-pills">
              {['vless', 'trojan', 'reality', 'ws', 'grpc'].map(protocol => (
                <button
                  key={protocol}
                  className={`protocol-pill ${protocols.includes(protocol) ? 'selected' : ''}`}
                  onClick={() => toggleProtocol(protocol)}
                >
                  {protocol.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Provider Selection */}
          <div className="form-group-compact">
            <label>📡 Ваш провайдер:</label>
            <div className="provider-grid">
              {Object.entries(PROVIDERS).map(([key, providerData]) => (
                <button
                  key={key}
                  className={`provider-card ${provider === key ? 'active' : ''}`}
                  onClick={() => setProvider(provider === key ? '' : key)}
                >
                  <div className="provider-logo-wrapper-small">
                    <img src={providerData.logo} alt={providerData.name} className="provider-logo-small" />
                  </div>
                  <span className="provider-label">{providerData.name}</span>
                </button>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* Center Test Button */}
      <div className="test-button-center">
        <button
          className={`btn-test-medium ${loading ? 'loading' : ''}`}
          onClick={activeTab === 'quick' ? runTest : generateConfig}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-small"></span>
              <span>Обработка...</span>
            </>
          ) : activeTab === 'quick' ? (
            <>
              <span>🚀</span>
              <span>Проверка сервера</span>
            </>
          ) : (
            <>
              <span>⚙️</span>
              <span>Сгенерировать</span>
            </>
          )}
        </button>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`result-panel-compact ${testResult.success ? 'success' : 'error'}`}>
          <div className="result-content">
            <div className="result-title">
              {testResult.success ? '✅ Пинг успешен' : '❌ Пинг не пройден'}
              {testResult.success && (
                <span className="result-time"> • {testResult.duration}ms</span>
              )}
            </div>
            {testResult.success && (
              <div className="result-hint">
                <div style={{ marginBottom: '6px', fontWeight: 600 }}>💡 Инструкция:</div>
                1. Откройте конфиг в Happ<br/>
                2. Проверьте все протоколы<br/>
                3. Вернитесь и нажмите результат<br/>
                <div style={{ marginTop: '8px', fontSize: '12px', fontStyle: 'italic' }}>
                  Вместе делаем сеть свободнее 🌐
                </div>
              </div>
            )}
            {testResult.error && (
              <div className="result-error">{testResult.error}</div>
            )}

            {/* Show URL and Happ button on success */}
            {testResult.success && testResult.url && (
              <>
                <div className="subscription-url-compact">
                  <div className="url-text">{testResult.url}</div>
                  <button
                    className="btn-icon"
                    onClick={() => copyToClipboard(testResult.url)}
                  >
                    📋
                  </button>
                </div>

                <button
                  className="happ-btn-testing"
                  onClick={async () => {
                    telegram.hapticMedium();
                    try {
                      const response = await api.post('/happ/generate-link', {
                        subscription_url: testResult.url,
                        provider: provider,
                        time_period: timeOfDay,
                        country: 'NL',
                        protocols: protocols,
                        source: 'testing'
                      });

                      if (response.data.connection_url) {
                        window.Telegram.WebApp.openLink(response.data.connection_url);
                      }
                    } catch (error) {
                      console.error('Failed to open in Happ:', error);
                      navigator.clipboard.writeText(testResult.url);
                      window.Telegram?.WebApp?.showAlert('URL скопирован в буфер обмена');
                    }
                  }}
                >
                  <img src="/images/providers/happ-logo.png" alt="Happ" className="happ-logo-testing" />
                  <span>Открыть в Happ</span>
                </button>
              </>
            )}
          </div>

          <div className="feedback-buttons-compact">
            <button className="btn-feedback success" onClick={() => submitFeedback(true)}>
              👍 Работает
            </button>
            <button className="btn-feedback danger" onClick={() => submitFeedback(false)}>
              👎 Не работает
            </button>
          </div>
        </div>
      )}

      {/* Generated Config Result */}
      {generatedConfig && (
        <div className="result-panel-compact success">
          <div className="result-content">
            <div className="result-title">📊 Конфигурация готова</div>
            <div className="subscription-url-compact">
              <div className="url-text">{generatedConfig.subscription_url}</div>
              <button
                className="btn-icon"
                onClick={() => copyToClipboard(generatedConfig.subscription_url)}
              >
                📋
              </button>
            </div>

            {/* Open in Happ button */}
            <button
              className="happ-btn-testing"
              onClick={() => {
                telegram.hapticMedium();

                // Use connection_url if available, otherwise fall back to clipboard copy
                if (generatedConfig.connection_url) {
                  // Open the connection URL which will redirect to Happ
                  window.Telegram.WebApp.openLink(generatedConfig.connection_url);
                } else {
                  // Fallback to clipboard copy
                  navigator.clipboard.writeText(generatedConfig.subscription_url)
                    .then(() => {
                      if (window.Telegram?.WebApp) {
                        window.Telegram.WebApp.showPopup({
                          title: '📋 URL скопирован!',
                          message: 'Конфигурация скопирована в буфер обмена.\n\nИнструкция:\n1. Откройте Happ\n2. Нажмите "+" → "Subscription"\n3. Вставьте скопированный URL\n4. Нажмите "Add"',
                          buttons: [{ type: 'ok' }]
                        });
                      }
                    })
                    .catch(() => {
                      if (window.Telegram?.WebApp) {
                        window.Telegram.WebApp.showAlert('Ошибка при копировании URL');
                      }
                    });
                }
              }}
            >
              <img src="/images/providers/happ-logo.png" alt="Happ" className="happ-logo-testing" />
              <span>Открыть в Happ</span>
            </button>
          </div>

          <div className="feedback-buttons-compact">
            <button className="btn-feedback success" onClick={() => submitFeedback(true)}>
              👍 Работает
            </button>
            <button className="btn-feedback danger" onClick={() => submitFeedback(false)}>
              👎 Не работает
            </button>
          </div>
        </div>
      )}

      {/* Compact Stats */}
      {stats && stats.total_tests > 0 && (
        <div className="panel-compact">
          <h3>📈 Общая статистика</h3>
          <div className="stats-compact">
            <div className="stat-item-compact">
              <span className="stat-provider-icon">📊</span>
              <span className="stat-provider-name">Все провайдеры</span>
              <span className="stat-rate">{stats.success_rate}%</span>
              <span className="stat-count">{stats.total_tests} тестов</span>
            </div>
            <div className="stat-summary">
              <div className="stat-detail">
                <span className="stat-label">✅ Работает:</span>
                <span className="stat-value">{stats.working}</span>
              </div>
              <div className="stat-detail">
                <span className="stat-label">❌ Не работает:</span>
                <span className="stat-value">{stats.not_working}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ ...feedbackModal, isOpen: false })}
        message={feedbackModal.message}
        type={feedbackModal.type}
      />
    </div>
  );
}

export default Testing;
